"use client";

import React from "react";
import { Bug, House, Wind, Rat, Zap, ShieldAlert, Droplets } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const pests = [{
  name: "Ants",
  description: "In Punta Gorda, ghost ants and fire ants thrive in our sandy soil and high humidity. These resilient colonies often migrate indoors during heavy Southwest Florida rains, requiring professional perimeter barriers to prevent persistent infestations in your kitchen and bathrooms.",
  icon: Bug
}, {
  name: "Termites",
  description: "Southwest Florida is a high-risk zone for both Subterranean and Drywood termites. With our local water table and warm climate, these 'silent destroyers' can compromise a home's structural integrity quickly. We provide specialized treatments tailored to Punta Gorda's unique environmental pressures.",
  icon: House
}, {
  name: "Cockroaches",
  description: "The American Cockroach, often called the Palmetto Bug, is a staple of the Florida ecosystem. In our waterfront communities, they frequently seek moisture and shelter inside homes. Our targeted treatments focus on the entry points common in local architecture to keep them outside.",
  icon: ShieldAlert
}, {
  name: "Spiders",
  description: "From common house spiders, orbweavers, and\xA0 concerning Black Widows found in dark corners of Florida garages, we manage them all. Our service includes de-webbing eaves and lanai / pool cages and applying specialized treatments that account for the active climate of the Peace River basin.",
  icon: Bug
}, {
  name: "Mosquitoes",
  description: "Living near the harbor or canal systems in Punta Gorda means dealing with intense mosquito pressure. Our seasonal control programs are designed to disrupt the breeding cycles that occur in the standing water and lush mangroves characteristic of our local landscapes in Punta Gorda, Cape Coral, and beyond.",
  icon: Droplets
}, {
  name: "Millipedes",
  description: "Following the heavy tropical downpours common in Southwest Florida, millipedes often emerge from the mulch in massive numbers. We apply specialized granular and liquid barriers that stop these 'thousand-leggers' (more like 200 legs) from invading your living spaces during the wet season.",
  icon: Bug
}, {
  name: "Rodents",
  description: "Roof rats are a significant concern for Punta Gorda homeowners, especially those with fruit trees or near wooded areas. They utilize overhanging branches to access attics. Our exclusion services seal the unique gaps found in Florida rooflines to keep your home rodent-free.",
  icon: Rat
}, {
  name: "Wasps & Hornets",
  description: "Paper wasps and mud daubers love the sheltered eaves of Southwest Florida homes. Given our year-round warmth, nesting activity never truly stops. We safely remove these stinging threats and apply deterrents to prevent them from rebuilding in high-traffic entryways.",
  icon: Zap
}];
export default function LocalPestGrid() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="localpestgrid" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4">
        <div ref={ref} className={`text-center mb-12 md:mb-16 transition-all duration-700 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-5xl font-default font-bold text-foreground mb-4">
            Common Pests in Punta Gorda & Southwest Florida
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-serif max-w-4xl mx-auto">Punta Gorda and the surrounding Southwest Florida region present a unique set of challenges for homeowners when it comes to pest management. Our subtropical climate, characterized by intense humidity and year-round warmth, creates an environment where pest activity never truly goes dormant. Unlike northern climates, our local ecosystem supports continuous breeding cycles for a variety of invasive species. The high water table and sandy soil conditions of Charlotte County influence how pests like subterranean termites and ants move through the landscape, often driving them toward the dry foundations of residential properties. Furthermore, our beautiful waterfront ecosystems and lush mangroves, while scenic, serve as prime breeding grounds for mosquitoes and midges. From the canal-front homes of Punta Gorda Isles to the active streets of Burnt Store Marina, the proximity to water and dense vegetation means that professional, localized expertise is essential. At Peace River Pest Control, we understand these specific environmental factors and provide targeted solutions that account for the unique biological pressures of our coastal Florida community.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {pests.map((pest, i) => {
          const {
            ref: cardRef,
            isVisible: isCardVisible
          } = useRevealOnScroll({
            threshold: 0.2,
            once: true
          });
          return <div key={i} data-index={i} ref={cardRef} className={`transition-all duration-500 transform ${isCardVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
            transitionDelay: `${i * 100}ms`
          }}>
                <Card className="h-full rounded-none border-2 border-primary shadow-none bg-card hover:bg-accent/5 transition-colors duration-300">
                  <CardHeader className="pb-4">
                    <div className="w-12 h-12 bg-primary/10 flex items-center justify-center mb-4">
                      <pest.icon className="w-6 h-6 text-primary" />
                    </div>
                    <CardTitle className="font-default text-2xl text-foreground">
                      <h3>{pest.name}</h3>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="font-serif text-muted-foreground leading-relaxed">
                      {pest.description}
                    </p>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>

        <div className="mt-16 text-center">
          <Button size="lg" className="rounded-full font-default text-lg px-8 py-6 h-auto shadow-md hover:shadow-lg transition-shadow" asChild>
            <Link to="/ContactPage">
              Get A Free Inspection
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}